import { useEffect, useRef, useState } from 'react';
import { Send, MessageSquare, AlertCircle, Users } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { chatApi, ApiError } from '@solara/api-client';
import type { ChatMessageDto, ChatThreadDto } from '@solara/api-client';

export default function AdminChatPage() {
  const { t } = useTranslation('p1');
  const [threads, setThreads] = useState<ChatThreadDto[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessageDto[]>([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (activeId) {
      void loadMessages(activeId);
      const interval = setInterval(() => void loadMessages(activeId), 8000);
      return () => clearInterval(interval);
    }
  }, [activeId]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [messages]);

  async function load() {
    try {
      const list = await chatApi.listThreads();
      setThreads(list);
      if (list[0]) setActiveId(list[0].id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function loadMessages(threadId: string) {
    try {
      setMessages(await chatApi.messages(threadId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function send() {
    if (!activeId || !input.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await chatApi.post(activeId, input);
      setInput('');
      await loadMessages(activeId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('chat.titleAdmin')}</h1>
        <p className="text-fg-muted text-sm">{t('chat.subtitleAdmin')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[18rem_1fr] gap-4">
        <Card className="rounded-2xl h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4" />
              {t('chat.threads')}
            </CardTitle>
          </CardHeader>
          <CardBody>
            {threads.length === 0 ? (
              <p className="py-3 text-xs text-fg-muted">{t('chat.noCustomerThreads')}</p>
            ) : (
              <ul className="space-y-1">
                {threads.map((th) => (
                  <li key={th.id}>
                    <button
                      onClick={() => setActiveId(th.id)}
                      className={`w-full rounded px-2 py-1 text-start text-sm ${
                        activeId === th.id ? 'bg-brand-100 text-brand-800' : 'hover:bg-surface-muted'
                      }`}
                    >
                      <p className="font-medium truncate">{th.title ?? t('chat.threadFallback')}</p>
                      <p className="text-[10px] text-fg-muted">
                        {th.lastMessageAt
                          ? new Date(th.lastMessageAt).toLocaleString()
                          : t('chat.noMessagesYet')}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <MessageSquare className="h-4 w-4" />
              {threads.find((th) => th.id === activeId)?.title ?? t('chat.noConversation')}
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div ref={scroller} className="h-[28rem] overflow-y-auto space-y-3 border border-border rounded p-3 bg-surface">
              {messages.length === 0 ? (
                <p className="py-12 text-center text-sm text-fg-muted">{t('chat.noMessages2')}</p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className="rounded bg-surface-muted px-3 py-2 text-sm">
                    <p className="whitespace-pre-wrap break-words">{m.body}</p>
                    <p className="mt-1 text-[10px] text-fg-muted">
                      {m.senderUserId.slice(0, 8)} · {new Date(m.createdAt).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
            {activeId && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void send();
                }}
                className="mt-3 flex items-center gap-2"
              >
                <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t('chat.replyPlaceholder')} className="flex-1" />
                <Button type="submit" disabled={busy || !input.trim()}>
                  <Send className="mr-1 h-3 w-3" />
                  Send
                </Button>
              </form>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
